import React, { useState } from 'react';

import { GlobalAccount, GUID } from '@ndvr/customer-api';

import { Spinner } from '@/components/Spinner';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreatePortfolio } from 'views/household/useCreatePortfolio';

interface CreatePortfolioModalProps {
    householdGUID: GUID<GlobalAccount>;
    onClose: () => void;
}

export const CreatePortfolioModal = ({ householdGUID, onClose }: CreatePortfolioModalProps) => {
    const { createPortfolio, defaultPortfolioName, isPending } = useCreatePortfolio(householdGUID);
    // Untouched (null) shows the default, which may only arrive once the household profile loads.
    const [enteredName, setEnteredName] = useState<string | null>(null);
    const portfolioName = enteredName ?? defaultPortfolioName;

    const isValid = !!portfolioName.trim() && !isPending;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!isValid) return;

        createPortfolio(portfolioName, { onSuccess: onClose });
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[425px]">
                <form onSubmit={handleSubmit}>
                    <DialogHeader>
                        <DialogTitle>Create Portfolio</DialogTitle>
                        <DialogDescription></DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="portfolioName">Portfolio Name</Label>
                            <Input
                                id="portfolioName"
                                value={portfolioName}
                                onChange={(e) => setEnteredName(e.target.value)}
                                placeholder="Enter portfolio name"
                                disabled={isPending}
                                autoFocus
                                required
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={!isValid}>
                            {isPending ? (
                                <>
                                    <Spinner size="sm" />
                                    Creating...
                                </>
                            ) : (
                                'Create Portfolio'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
